import { defineBoot } from '#q-app/wrappers'
import { Quasar, LocalStorage, date } from 'quasar'
import quasarLangEn from 'quasar/lang/en-US'
import quasarLangFi from 'quasar/lang/fi'
import quasarLangDe from 'quasar/lang/de'
import quasarLangNl from 'quasar/lang/nl'
import { createI18n } from 'vue-i18n'

import { bookingApiErrorMessages } from 'src/i18n/booking-api-error-messages'

export { bookingApiErrorMessages }

export type Locale = 'en' | 'fi' | 'de' | 'nl'

export const quasarLanguages = {
  en: quasarLangEn,
  fi: quasarLangFi,
  de: quasarLangDe,
  nl: quasarLangNl,
}

const detectedLocale: string =
  LocalStorage.getItem('locale') || LocalStorage.getItem('country_code') || window.navigator.language.slice(0, 2)

if (Object.keys(quasarLanguages).includes(detectedLocale)) {
  Quasar.lang.set(quasarLanguages[detectedLocale as Locale])
}

export const i18n = createI18n({
  locale: detectedLocale,
  fallbackLocale: 'en',
  globalInjection: true,
  legacy: false,
  messages: {
    en: {
      ...bookingApiErrorMessages.en,
      login: {
        form: {
          email_address: 'Email address',
          password: 'Password',
          submit: 'Log in',
        },
        error: {
          INVALID_CREDENTIALS: 'Invalid credentials',
          UNKNOWN: 'Unknown error',
        },
      },
      geocode_address_failure: 'Failed to determine coordinates of address',
      reverse_geocode_failure: 'Failed to determine address of location',
      address_not_found: "Couldn't find address",
      unknown_address: 'Unknown address',
      geolocation_not_supported: 'Geolocation is not supported by this browser',
      geolocation_permission_denied: 'Permission to use geolocation was denied',
      geolocation_failure: 'Failed to determine current location',
      geolocation_timeout: 'Timed out while determining current location',
    },
    fi: {
      ...bookingApiErrorMessages.fi,
      login: {
        form: {
          email_address: 'Sähköpostiosoite',
          password: 'Salasana',
          submit: 'Kirjaudu sisään',
        },
        error: {
          INVALID_CREDENTIALS: 'Virheellinen sähköpostiosoite tai salasana',
          UNKNOWN: 'Tuntematon virhe',
        },
      },
      geocode_address_failure: 'Osoitteen koordinaatteja ei onnistuttu määrittämään',
      reverse_geocode_failure: 'Sijainnin osoitetta ei onnistuttu määrittämään',
      address_not_found: 'Osoitetta ei löytynyt',
      unknown_address: 'Tuntematon osoite',
      geolocation_not_supported: 'Selaimesi ei tue sijaintitietojen käyttöä',
      geolocation_permission_denied: 'Sijaintitietojen käyttöoikeus evätty',
      geolocation_failure: 'Nykyisen sijainnin määrittäminen epäonnistui',
      geolocation_timeout: 'Aikakatkaisu sijainnin määrittämisessä',
    },
    de: {
      ...bookingApiErrorMessages.de,
      login: {
        form: {
          email_address: 'Email-Adresse',
          password: 'Passwort',
          submit: 'Anmelden',
        },
        error: {
          INVALID_CREDENTIALS: 'Ungültige Email oder Passwort',
          UNKNOWN: 'Unbekannter Fehler',
        },
      },
      geocode_address_failure: 'Koordinaten der Adresse konnten nicht ermittelt werden',
      reverse_geocode_failure: 'Adresse des Standorts konnte nicht ermittelt werden',
      address_not_found: 'Adresse nicht gefunden',
      unknown_address: 'Unbekannte Adresse',
      geolocation_not_supported: 'Geolokalisierung wird von diesem Browser nicht unterstützt',
      geolocation_permission_denied: 'Berechtigung zur Verwendung des Standorts wurde verweigert',
      geolocation_failure: 'Aktueller Standort konnte nicht ermittelt werden',
      geolocation_timeout: 'Zeitüberschreitung bei der Ermittlung des aktuellen Standorts',
    },
    nl: {
      ...bookingApiErrorMessages.nl,
      login: {
        form: {
          email_address: 'E-mailadres',
          password: 'Wachtwoord',
          submit: 'Inloggen',
        },
        error: {
          INVALID_CREDENTIALS: 'Ongeldig e-mailadres of wachtwoord',
          UNKNOWN: 'Onbekende fout',
        },
      },
      geocode_address_failure: 'Kan de coördinaten van het adres niet bepalen',
      reverse_geocode_failure: 'Kan het adres van de locatie niet bepalen',
      address_not_found: 'Adres niet gevonden',
      unknown_address: 'Onbekend adres',
      geolocation_not_supported: 'Geolocatie wordt niet ondersteund door deze browser',
      geolocation_permission_denied: 'Toestemming voor het gebruik van geolocatie is geweigerd',
      geolocation_failure: 'Huidige locatie kan niet worden bepaald',
      geolocation_timeout: 'Time-out bij het bepalen van de huidige locatie',
    },
  },
})

export function setLocale(locale: Locale) {
  i18n.global.locale.value = locale
  LocalStorage.set('locale', locale)
  LocalStorage.set('user_changed_locale', true)
  Quasar.lang.set(quasarLanguages[locale])
}

export function setCountryCode(countryCode: string) {
  LocalStorage.set('country_code', countryCode)
  if (!LocalStorage.getItem('user_changed_locale')) {
    setLocale(countryCode as Locale)
  }
}

const daysTranslations = {
  en: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
  fi: ['Sunnuntai', 'Maanantai', 'Tiistai', 'Keskiviikko', 'Torstai', 'Perjantai', 'Lauantai'],
  de: ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'],
  nl: ['Zondag', 'Maandag', 'Dinsdag', 'Woensdag', 'Donderdag', 'Vrijdag', 'Zaterdag'],
}

const daysShortTranslations = {
  en: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
  fi: ['Su', 'Ma', 'Ti', 'Ke', 'To', 'Pe', 'La'],
  de: ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'],
  nl: ['Zo', 'Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za'],
}

const monthsTranslations = {
  en: [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ],
  fi: [
    'Tammikuu',
    'Helmikuu',
    'Maaliskuu',
    'Huhtikuu',
    'Toukokuu',
    'Kesäkuu',
    'Heinäkuu',
    'Elokuu',
    'Syyskuu',
    'Lokakuu',
    'Marraskuu',
    'Joulukuu',
  ],
  de: [
    'Januar',
    'Februar',
    'März',
    'April',
    'Mai',
    'Juni',
    'Juli',
    'August',
    'September',
    'Oktober',
    'November',
    'Dezember',
  ],
  nl: [
    'Januari',
    'Februari',
    'Maart',
    'April',
    'Mei',
    'Juni',
    'Juli',
    'Augustus',
    'September',
    'Oktober',
    'November',
    'December',
  ],
}
const monthsShortTranslations = {
  en: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
  fi: ['Tammi', 'Helmi', 'Maalis', 'Huhti', 'Touko', 'Kesä', 'Heinä', 'Elo', 'Syys', 'Loka', 'Marras', 'Joulu'],
  de: ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'],
  nl: ['Jan', 'Feb', 'Mrt', 'Apr', 'Mei', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec'],
}

export function formatDate(dateTime: Date | string, format: string) {
  const localeConfig = {
    days: daysTranslations[i18n.global.locale.value] || daysTranslations.en,
    daysShort: daysShortTranslations[i18n.global.locale.value] || daysShortTranslations.en,
    months: monthsTranslations[i18n.global.locale.value] || monthsTranslations.en,
    monthsShort: monthsShortTranslations[i18n.global.locale.value] || monthsShortTranslations.en,
  }
  return date.formatDate(dateTime, format, localeConfig)
}


export default defineBoot(({ app }) => {
  app.use(i18n)
})
